import React, { useState, useEffect } from "react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, XCircle, AlertTriangle, Loader2, Database, Users } from "lucide-react";
import { SelectAllPagesService, SelectAllPagesResult } from "./services/selectAllPagesService";
import type { BaseEntity } from "../UniversalCRMTable/types";

export interface SelectAllPagesModalProps<T extends BaseEntity> {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (result: SelectAllPagesResult<T>) => void;
  fetchPage: (
    page: number,
    pageSize: number
  ) => Promise<{ data: T[]; totalCount: number; hasNextPage: boolean }>;
  fetchPageGraphQL?: (
    cursor?: string,
    limit?: number
  ) => Promise<{ data: T[]; nextCursor?: string; hasNextPage: boolean }>;
  pageSize?: number;
  maxPages?: number;
  title?: string;
  description?: string;
}

interface ProgressState {
  currentPage: number;
  totalPages: number;
  currentCount: number;
  percentage: number;
  isComplete: boolean;
  errors: Array<{ page: number; error: Error }>;
}

export function SelectAllPagesModal<T extends BaseEntity>({
  isOpen,
  onClose,
  onComplete,
  fetchPage,
  fetchPageGraphQL,
  pageSize = 100,
  maxPages = 1000,
  title = "Select All Pages",
  description = "Fetching all data across all pages for bulk actions...",
}: SelectAllPagesModalProps<T>) {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<ProgressState>({
    currentPage: 0,
    totalPages: 0,
    currentCount: 0,
    percentage: 0,
    isComplete: false,
    errors: [],
  });
  const [result, setResult] = useState<SelectAllPagesResult<T> | null>(null);

  const handleStart = async () => {
    setIsLoading(true);
    setProgress({
      currentPage: 0,
      totalPages: 0,
      currentCount: 0,
      percentage: 0,
      isComplete: false,
      errors: [],
    });

    try {
      const progressCallback = SelectAllPagesService.createProgressCallback((progressData) => {
        setProgress((prev) => ({
          ...prev,
          currentPage: progressData.currentPage,
          totalPages: progressData.totalPages,
          currentCount: progressData.currentCount,
          percentage: progressData.percentage,
          isComplete:
            progressData.currentPage >= progressData.totalPages && progressData.totalPages > 0,
        }));
      });

      const errorCallback = (error: Error, page: number) => {
        setProgress((prev) => ({
          ...prev,
          errors: [...prev.errors, { page, error }],
        }));
      };

      let fetchResult: SelectAllPagesResult<T>;

      if (fetchPageGraphQL) {
        // Use GraphQL-style pagination
        fetchResult = await SelectAllPagesService.fetchAllPagesGraphQL(fetchPageGraphQL, {
          pageSize,
          maxPages,
          onProgress: progressCallback,
          onError: errorCallback,
        });
      } else {
        // Use standard pagination
        fetchResult = await SelectAllPagesService.fetchAllPages(fetchPage, {
          pageSize,
          maxPages,
          onProgress: progressCallback,
          onError: errorCallback,
        });
      }

      setResult(fetchResult);
      setProgress((prev) => ({ ...prev, isComplete: true }));
    } catch (error) {
      console.error("Failed to fetch all pages:", error);
      setProgress((prev) => ({
        ...prev,
        errors: [...prev.errors, { page: 0, error: error as Error }],
        isComplete: true,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = () => {
    if (result) {
      onComplete(result);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setProgress({
        currentPage: 0,
        totalPages: 0,
        currentCount: 0,
        percentage: 0,
        isComplete: false,
        errors: [],
      });
      setResult(null);
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={description}
      maxWidth="md"
      side="bottom"
    >
      <div className="space-y-4">
        {!isLoading && !progress.isComplete && (
          <div className="text-center">
            <Button onClick={handleStart} className="w-full">
              <Database className="h-4 w-4 mr-2" />
              Start Fetching All Pages
            </Button>
          </div>
        )}

        {isLoading && (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{progress.percentage}%</span>
              </div>
              <Progress value={progress.percentage} className="w-full" />
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Items: {progress.currentCount.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                <span>
                  Page: {progress.currentPage}/{progress.totalPages || "?"}
                </span>
              </div>
            </div>

            {progress.errors.length > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {progress.errors.length} page(s) failed to load. Continuing with available data.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {progress.isComplete && result && (
          <div className="space-y-4">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <h3 className="font-semibold text-lg">Fetch Complete!</h3>
              <p className="text-sm text-muted-foreground">
                Successfully fetched {result.allItems.length.toLocaleString()} items
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Total Items: {result.allItems.length.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                <span>Pages: {result.pagesFetched}</span>
              </div>
            </div>

            {result.errors.length > 0 && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {result.errors.length} page(s) had errors but were skipped.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button onClick={handleComplete} className="flex-1">
                <CheckCircle className="h-4 w-4 mr-2" />
                Continue with Bulk Actions
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {progress.isComplete && !result && (
          <div className="text-center space-y-4">
            <XCircle className="h-12 w-12 text-red-500 mx-auto" />
            <h3 className="font-semibold text-lg">Fetch Failed</h3>
            <p className="text-sm text-muted-foreground">
              Unable to fetch all pages. Please try again.
            </p>
            <Button variant="outline" onClick={handleCancel}>
              Close
            </Button>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}
